import { inject, Injectable } from '@angular/core';
import { Upgrade } from '@core/models';
import { AdobeDataLayerService } from '@core/services/adobe-data-layer-service.service';
import { AnalyticsService } from '@core/services/analytics';
import { PrivateSellingService } from '@core/services/private-selling.service';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UpgradesService {
  private privateSellingService = inject(PrivateSellingService);
  private analyticsService = inject(AnalyticsService);
  private adobeDataLayerService = inject(AdobeDataLayerService);

  // `undefined` until an event with `upgrades` is loaded (assignees:
  // select-seat/checkout/summary); consumers guard with `?.length`.
  upgrades: Upgrade[] | undefined;
  totalCartAndFiltersSeats$ = new Subject<number>(); // Cada vez que cambia (se hace un ".next") se lanza el this.checkTotalSeatsForUpgrades(totalCartSeats) desde upgrades.component.ts
  upgradesCall: ReturnType<typeof setTimeout>;

  reachingUpgrade: Upgrade | null = null;
  acquiredUpgrade: Upgrade | null = null;

  availableUpgrades: Upgrade[] = [];
  acquiredUpgrades: Upgrade[] = [];
  previousTotalSeats: number;
  // Analytics crossing baseline, deliberately separate from previousTotalSeats: only real
  // (user-intent) CART emissions update it. The quantity box/filters path drives
  // previousTotalSeats for the toasts but must not shift the baseline — otherwise a "4"
  // typed while the cart holds 3 seats reports seats_in_cart=4 / fakes a crossing.
  private previousCartSeats: number | undefined;
  // >0 while a synthetic cart mutation (restore after a seat-lock bounce, tutorial
  // pre-fill) is emitting through selectedSeats$. Analytics-only gate: the reward toasts
  // keep working, upgrade_shown/upgrade_acquired must not count a restore as a crossing.
  private syntheticCartEmissionDepth = 0;

  beginSyntheticCartEmission(): void {
    this.syntheticCartEmissionDepth += 1;
  }

  endSyntheticCartEmission(): void {
    this.syntheticCartEmissionDepth = Math.max(
      0,
      this.syntheticCartEmissionDepth - 1
    );
  }

  get isSyntheticCartEmission(): boolean {
    return this.syntheticCartEmissionDepth > 0;
  }

  sendLastUpgradesCall(numSeats: number) {
    if (this.upgradesCall) {
      clearTimeout(this.upgradesCall);
    }
    // Updating totalCartAndFiltersSeats number for Upgrades
    this.upgradesCall = setTimeout(() => {
      this.totalCartAndFiltersSeats$.next(numSeats);
    }, 1000);
  }

  sortUpgradesByGoal(upgrades: Upgrade[]) {
    if (upgrades?.length) {
      upgrades.sort((a, b) => a.goal - b.goal);
    }
  }

  checkCartSeatsForUpgradesList(totalCartSeats: number) {
    if (!this.upgrades?.length) return;

    this.sortUpgradesByGoal(this.upgrades);
    // Reset Upgrades lists, available and acquired
    this.availableUpgrades = [];
    this.acquiredUpgrades = [];

    for (const upgrade of this.upgrades) {
      // Set Upgrades lists, available and acquired
      if (upgrade.goal <= totalCartSeats) {
        this.acquiredUpgrades.push(upgrade);
      } else {
        this.availableUpgrades.push(upgrade);
      }
    }
  }

  /**
   * Drives the reward toasts AND the upgrade_shown/upgrade_acquired analytics.
   *
   * `withAnalytics` is false for the quantity-box/filters emission
   * (totalCartAndFiltersSeats$): that number is search intent, not the cart, and using it
   * produced upgrade_acquired with a seat count the patron never actually had (test-report
   * defect 7). Analytics therefore ride on real cart emissions only.
   *
   * Within a cart emission, an event only fires when the threshold is crossed UPWARD
   * relative to the previous cart size (previousCartSeats) — re-crossings downward
   * (4 -> 3 past a minSeatsToShow of 3 used to fire "reaching" again) and synthetic
   * (restore/tutorial) emissions are filtered out here; AnalyticsService additionally
   * deduplicates per upgrade per journey.
   */
  checkTotalSeatsForUpgradesMessages(
    totalSeats: number,
    withAnalytics: boolean = true
  ) {
    if (!this.upgrades?.length) return;
    if (
      this.privateSellingService.isEnabled() &&
      !this.privateSellingService.isUserInWhitelist()
    ) {
      return;
    }

    this.sortUpgradesByGoal(this.upgrades);

    let availableUps = [];
    let acquiredUps = [];
    for (const upgrade of this.upgrades) {
      // Set Upgrades lists, available and acquired
      if (upgrade.goal <= totalSeats) {
        acquiredUps.push(upgrade);
      } else {
        availableUps.push(upgrade);
      }
    }

    const gameId = this.adobeDataLayerService.getEventId() || undefined;
    const analyticsEnabled = withAnalytics && !this.isSyntheticCartEmission;
    const crossingBaseline = this.previousCartSeats ?? 0;
    const crossedUpwardTo = (threshold: number): boolean =>
      analyticsEnabled &&
      totalSeats > crossingBaseline &&
      threshold > crossingBaseline &&
      threshold <= totalSeats;

    for (const upgrade of this.upgrades) {
      // Si el num seats coincide con un Goal
      if (upgrade.goal === totalSeats) {
        this.acquiredUpgrade = upgrade;
        setTimeout(() => (this.acquiredUpgrade = null), 3000);
        if (crossedUpwardTo(upgrade.goal)) {
          this.analyticsService.trackUpgradeAcquired({
            upgradeId: String(upgrade.id),
            upgradeName: upgrade.name,
            goal: upgrade.goal,
            seatsInCart: totalSeats,
            gameId,
          });
        }
        // Si el num seats coincide con un minSeatsToShow
      } else if (upgrade.minSeatsToShow === totalSeats) {
        this.reachingUpgrade = upgrade;
        setTimeout(() => (this.reachingUpgrade = null), 3000);
        if (crossedUpwardTo(upgrade.minSeatsToShow)) {
          this.analyticsService.trackUpgradeShown({
            upgradeId: String(upgrade.id),
            upgradeName: upgrade.name,
            goal: upgrade.goal,
            minSeatsToShow: upgrade.minSeatsToShow,
            seatsInCart: totalSeats,
            gameId,
          });
        }
      }
    }

    // Si el num seats tiene una diferencia con el num previo de más de 1 (se ha introducido por el input manualmente)
    if (
      this.previousTotalSeats - totalSeats < -1 ||
      this.previousTotalSeats - totalSeats > 1
    ) {
      // A typed-quantity jump can cross one or more goals without ever landing exactly on
      // one — the `upgrade.goal === totalSeats` check above only catches a step-by-step ±1
      // change. Without this, jumping e.g. 2 -> 5 seats past a goal of 3 would silently skip
      // upgrade_acquired for it. trackUpgradeAcquired's own reportedUpgradeAcquisitions dedup
      // makes it safe to call for a goal that also happens to satisfy the equality check.
      if (totalSeats > this.previousTotalSeats) {
        for (const upgrade of this.upgrades) {
          const wasCrossed =
            upgrade.goal > this.previousTotalSeats &&
            upgrade.goal <= totalSeats;
          if (!wasCrossed) {
            continue;
          }
          this.acquiredUpgrade = upgrade;
          setTimeout(() => (this.acquiredUpgrade = null), 3000);
          if (crossedUpwardTo(upgrade.goal)) {
            this.analyticsService.trackUpgradeAcquired({
              upgradeId: String(upgrade.id),
              upgradeName: upgrade.name,
              goal: upgrade.goal,
              seatsInCart: totalSeats,
              gameId,
            });
          }
        }
      }

      if (availableUps[0] && availableUps[0].minSeatsToShow < totalSeats) {
        this.reachingUpgrade = availableUps[0];
        setTimeout(() => (this.reachingUpgrade = null), 3000);
        if (crossedUpwardTo(availableUps[0].minSeatsToShow)) {
          this.analyticsService.trackUpgradeShown({
            upgradeId: String(availableUps[0].id),
            upgradeName: availableUps[0].name,
            goal: availableUps[0].goal,
            minSeatsToShow: availableUps[0].minSeatsToShow,
            seatsInCart: totalSeats,
            gameId,
          });
        }
      }
    }

    // Store previous seats count
    this.previousTotalSeats = totalSeats;
    if (withAnalytics) {
      // Cart path — even when its analytics were suppressed (synthetic restore): the restored
      // seats ARE the current cart, so the next user mutation must cross from this number.
      this.previousCartSeats = totalSeats;
    }
  }
}
